import type { ReactNode } from "react";

import {
  collectFillableGroups,
  defaultIdFactory,
  distributeIntoSkeleton,
  LAYOUT_SCHEMA_VERSION,
  type Layout,
  type LayoutNode,
  type PanelRef,
  type Split,
  type TabGroup,
} from "@/shell/layout";
import type { PanelId } from "@/shell/PanelRegistry";

/* ============================================================ */
/* Layout templates — picker visuel style Windows Snap Layouts   */
/*                                                                */
/* Chaque template a :                                            */
/*   - id stable                                                  */
/*   - nom court (label thumbnail)                                */
/*   - cells : nombre de cellules → utilise pour rotate les       */
/*     panneaux par defaut quand on applique le template          */
/*   - Thumbnail : composant React qui dessine la mini-grille     */
/*   - build(panels) : construit le Layout tree                   */
/* ============================================================ */

/**
 * Pick le panel pour la cellule `i` du template. Si l'appelant fournit
 * explicitement un panel a cet index, on l'utilise — sinon on retombe sur
 * "empty" (slot vide qui rend un bouton "+ Ajouter un panneau", cf
 * EmptyPanel).
 *
 * On NE rotate PAS entre plusieurs panels par defaut (anciennement
 * "conversations" / "empty") : appliquer un layout 6 cellules ne doit pas
 * peupler la moitie de l'ecran de panneaux Conversations non desires.
 * L'user choisit le contenu de chaque cellule via le picker apres coup.
 */
function pickPanel(panels: PanelRef[], i: number): PanelRef {
  return panels[i] ?? ("empty" satisfies PanelId);
}

function leaf(panelId: PanelRef): TabGroup {
  return {
    kind: "tabs",
    id: defaultIdFactory(),
    panels: [{ id: defaultIdFactory(), panelId }],
    activeIndex: 0,
  };
}

function rowSplit(children: LayoutNode[], sizes: number[]): Split {
  return {
    kind: "split",
    id: defaultIdFactory(),
    direction: "row",
    children,
    sizes,
  };
}

function colSplit(children: LayoutNode[], sizes: number[]): Split {
  return {
    kind: "split",
    id: defaultIdFactory(),
    direction: "column",
    children,
    sizes,
  };
}

function wrap(root: LayoutNode): Layout {
  return { version: LAYOUT_SCHEMA_VERSION, root };
}

/* ============================================================ */
/* Thumbnails — mini-diagrammes en CSS grid                       */
/*                                                                */
/* Des aplats d'encre (`bg-fg/20`) : ils s'adaptent au thème, clair */
/* comme sombre. Plus de contour « de la même couleur » (coque,   */
/* 01-11-2026) : la forme de la disposition, rien d'autre.        */
/* ============================================================ */

const cellClass = "bg-fg/20";

function ThumbWrap({ children }: { children: ReactNode }) {
  return <div className="grid h-full w-full gap-[2px]">{children}</div>;
}

const thumbnails = {
  single: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-1 gap-[2px] h-full">
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  cols2: (
    <ThumbWrap>
      <div className="grid grid-cols-2 grid-rows-1 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  rows2: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-2 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  cols3: (
    <ThumbWrap>
      <div className="grid grid-cols-3 grid-rows-1 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  rows3: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-3 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  leftBig: (
    <ThumbWrap>
      <div className="grid grid-cols-[2fr_1fr] grid-rows-1 gap-[2px] h-full">
        <div className={cellClass} />
        <div className="grid grid-rows-2 gap-[2px]">
          <div className={cellClass} />
          <div className={cellClass} />
        </div>
      </div>
    </ThumbWrap>
  ),
  rightBig: (
    <ThumbWrap>
      <div className="grid grid-cols-[1fr_2fr] grid-rows-1 gap-[2px] h-full">
        <div className="grid grid-rows-2 gap-[2px]">
          <div className={cellClass} />
          <div className={cellClass} />
        </div>
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  grid2x2: (
    <ThumbWrap>
      <div className="grid grid-cols-2 grid-rows-2 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  cols3Bot1: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-[2fr_1fr] gap-[2px] h-full">
        <div className="grid grid-cols-3 gap-[2px]">
          <div className={cellClass} />
          <div className={cellClass} />
          <div className={cellClass} />
        </div>
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  top1Cols3: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-[1fr_2fr] gap-[2px] h-full">
        <div className={cellClass} />
        <div className="grid grid-cols-3 gap-[2px]">
          <div className={cellClass} />
          <div className={cellClass} />
          <div className={cellClass} />
        </div>
      </div>
    </ThumbWrap>
  ),
  grid3x2: (
    <ThumbWrap>
      <div className="grid grid-cols-3 grid-rows-2 gap-[2px] h-full">
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
  ae: (
    <ThumbWrap>
      <div className="grid grid-cols-1 grid-rows-[2fr_1fr] gap-[2px] h-full">
        <div className="grid grid-cols-[1fr_2fr_1fr] gap-[2px]">
          <div className={cellClass} />
          <div className={cellClass} />
          <div className={cellClass} />
        </div>
        <div className={cellClass} />
      </div>
    </ThumbWrap>
  ),
};

/* ============================================================ */
/* Templates                                                      */
/* ============================================================ */

export interface LayoutTemplate {
  id: string;
  name: string;
  cells: number;
  thumbnail: ReactNode;
  build: (panels: PanelRef[]) => Layout;
}

export const LAYOUT_TEMPLATES: LayoutTemplate[] = [
  {
    id: "single",
    name: "Full screen",
    cells: 1,
    thumbnail: thumbnails.single,
    build: (p) => wrap(leaf(pickPanel(p, 0))),
  },
  {
    id: "cols-2",
    name: "2 columns",
    cells: 2,
    thumbnail: thumbnails.cols2,
    build: (p) =>
      wrap(rowSplit([leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1))], [50, 50])),
  },
  {
    id: "rows-2",
    name: "2 rows",
    cells: 2,
    thumbnail: thumbnails.rows2,
    build: (p) =>
      wrap(colSplit([leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1))], [50, 50])),
  },
  {
    id: "cols-3",
    name: "3 columns",
    cells: 3,
    thumbnail: thumbnails.cols3,
    build: (p) =>
      wrap(
        rowSplit(
          [leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1)), leaf(pickPanel(p, 2))],
          [33, 34, 33]
        )
      ),
  },
  {
    id: "rows-3",
    name: "3 rows",
    cells: 3,
    thumbnail: thumbnails.rows3,
    build: (p) =>
      wrap(
        colSplit(
          [leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1)), leaf(pickPanel(p, 2))],
          [33, 34, 33]
        )
      ),
  },
  {
    id: "left-big",
    name: "Wide left",
    cells: 3,
    thumbnail: thumbnails.leftBig,
    build: (p) =>
      wrap(
        rowSplit(
          [
            leaf(pickPanel(p, 0)),
            colSplit([leaf(pickPanel(p, 1)), leaf(pickPanel(p, 2))], [50, 50]),
          ],
          [66, 34]
        )
      ),
  },
  {
    id: "right-big",
    name: "Wide right",
    cells: 3,
    thumbnail: thumbnails.rightBig,
    build: (p) =>
      wrap(
        rowSplit(
          [
            colSplit([leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1))], [50, 50]),
            leaf(pickPanel(p, 2)),
          ],
          [34, 66]
        )
      ),
  },
  {
    id: "grid-2x2",
    name: "Grid 2×2",
    cells: 4,
    thumbnail: thumbnails.grid2x2,
    build: (p) =>
      wrap(
        colSplit(
          [
            rowSplit(
              [leaf(pickPanel(p, 0)), leaf(pickPanel(p, 1))],
              [50, 50]
            ),
            rowSplit(
              [leaf(pickPanel(p, 2)), leaf(pickPanel(p, 3))],
              [50, 50]
            ),
          ],
          [50, 50]
        )
      ),
  },
  {
    id: "cols-3-bot",
    name: "3 top + 1 bottom",
    cells: 4,
    thumbnail: thumbnails.cols3Bot1,
    build: (p) =>
      wrap(
        colSplit(
          [
            rowSplit(
              [
                leaf(pickPanel(p, 0)),
                leaf(pickPanel(p, 1)),
                leaf(pickPanel(p, 2)),
              ],
              [33, 34, 33]
            ),
            leaf(pickPanel(p, 3)),
          ],
          [66, 34]
        )
      ),
  },
  {
    id: "top-cols-3",
    name: "1 top + 3 bottom",
    cells: 4,
    thumbnail: thumbnails.top1Cols3,
    build: (p) =>
      wrap(
        colSplit(
          [
            leaf(pickPanel(p, 0)),
            rowSplit(
              [
                leaf(pickPanel(p, 1)),
                leaf(pickPanel(p, 2)),
                leaf(pickPanel(p, 3)),
              ],
              [33, 34, 33]
            ),
          ],
          [34, 66]
        )
      ),
  },
  {
    id: "grid-3x2",
    name: "Grid 3×2",
    cells: 6,
    thumbnail: thumbnails.grid3x2,
    build: (p) =>
      wrap(
        colSplit(
          [
            rowSplit(
              [
                leaf(pickPanel(p, 0)),
                leaf(pickPanel(p, 1)),
                leaf(pickPanel(p, 2)),
              ],
              [33, 34, 33]
            ),
            rowSplit(
              [
                leaf(pickPanel(p, 3)),
                leaf(pickPanel(p, 4)),
                leaf(pickPanel(p, 5)),
              ],
              [33, 34, 33]
            ),
          ],
          [50, 50]
        )
      ),
  },
  {
    id: "ae-classic",
    name: "After Effects",
    cells: 4,
    thumbnail: thumbnails.ae,
    build: (p) =>
      wrap(
        colSplit(
          [
            rowSplit(
              [
                leaf(pickPanel(p, 0)),
                leaf(pickPanel(p, 1)),
                leaf(pickPanel(p, 2)),
              ],
              [25, 50, 25]
            ),
            leaf(pickPanel(p, 3)),
          ],
          [70, 30]
        )
      ),
  },
];

/** Lookup par id (utilise par les actions / persistence). */
export const LAYOUT_TEMPLATES_BY_ID: Record<string, LayoutTemplate> =
  Object.fromEntries(LAYOUT_TEMPLATES.map((t) => [t.id, t]));

/**
 * Applique un template de disposition au layout courant EN PRESERVANT les
 * panneaux actifs.
 *
 * Contrairement a `tpl.build([])` (qui remplit toutes les cellules de
 * placeholders "empty" et jette les vues actives), cette fonction :
 *   - reinjecte chaque group rempli du layout courant dans une cellule du
 *     nouveau template (vues / params / onglets multiples preserves) ;
 *   - laisse un placeholder "Ajouter un panneau" dans les cellules en trop ;
 *   - si le nouveau template a MOINS de cellules que de panneaux remplis,
 *     fusionne le surplus comme onglets dans la 1ere cellule (rien n'est
 *     perdu).
 *
 * Si le layout courant n'a aucun panneau rempli (workspace vierge), on
 * retourne simplement le squelette du template (placeholders partout) — le
 * comportement historique.
 */
export function applyTemplatePreservingPanels(
  current: Layout,
  tpl: LayoutTemplate
): Layout {
  const skeleton = tpl.build([]);
  const sources = collectFillableGroups(current);
  if (sources.length === 0) return skeleton;
  return distributeIntoSkeleton(skeleton, sources, "first");
}
